import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  MenuItem,
  Select
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import PublishIcon from '@mui/icons-material/Publish';
import UnpublishedIcon from '@mui/icons-material/Unpublished';
import useGet from 'hooks/useGet';
import useDelete from 'hooks/useDelete';
import apiClient from 'utils/apiClient';
import { useAuth } from 'pages/AuthProvider';

const buildEndpoint = ({ page, limit, search, tag, status }) => {
  const params = new URLSearchParams();
  params.set('includeDrafts', 'true');
  params.set('page', String(page + 1));
  params.set('limit', String(limit));
  if (search) params.set('search', search);
  if (tag) params.set('tag', tag);
  if (status === 'published') params.set('isPublished', 'true');
  if (status === 'draft') params.set('isPublished', 'false');
  return `/api/blogs?${params.toString()}`;
};

const formatDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString();
};

const BlogList = () => {
  const navigate = useNavigate();
  const { canAccess } = useAuth();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState('');
  const [tag, setTag] = useState('');
  const [status, setStatus] = useState('all');
  const endpoint = useMemo(
    () => buildEndpoint({ page, limit: rowsPerPage, search: search.trim(), tag: tag.trim(), status }),
    [page, rowsPerPage, search, tag, status]
  );

  const { data, loading, mutate } = useGet(endpoint);
  const { remove } = useDelete('/api/blogs');

  const blogs = data?.blogs || [];
  const total = data?.total ?? blogs.length;

  const handleDelete = async (blog) => {
    const confirmed = window.confirm(`Delete "${blog.title || 'this blog'}"?`);
    if (!confirmed) return;
    await remove(blog._id, { revalidate: false });
    await mutate();
  };

  const handleTogglePublish = async (blog) => {
    await apiClient.patch(`/api/blogs/${blog._id}/published`);
    await mutate();
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h5">Blogs</Typography>
        {canAccess('blogs', 'create') && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/blogs/new')}>
            New Blog
          </Button>
        )}
      </Box>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            size="small"
            label="Search"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setPage(0);
            }}
          />
          <TextField
            size="small"
            label="Tag"
            value={tag}
            onChange={(event) => {
              setTag(event.target.value);
              setPage(0);
            }}
          />
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel id="blog-status-label">Status</InputLabel>
            <Select
              labelId="blog-status-label"
              label="Status"
              value={status}
              onChange={(event) => {
                setStatus(event.target.value);
                setPage(0);
              }}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="published">Published</MenuItem>
              <MenuItem value="draft">Draft</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Title</TableCell>
              <TableCell>Slug</TableCell>
              <TableCell>Tags</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Published</TableCell>
              <TableCell>Views</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7}>Loading blogs...</TableCell>
              </TableRow>
            ) : blogs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7}>No blogs found.</TableCell>
              </TableRow>
            ) : (
              blogs.map((blog) => (
                <TableRow key={blog._id}>
                  <TableCell>{blog.title || '-'}</TableCell>
                  <TableCell>{blog.slug || '-'}</TableCell>
                  <TableCell>
                    {(blog.tags || []).length === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        -
                      </Typography>
                    ) : (
                      (blog.tags || []).slice(0, 3).map((tagItem) => (
                        <Chip key={`${blog._id}-${tagItem}`} label={tagItem} size="small" sx={{ mr: 0.5 }} />
                      ))
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={blog.isPublished ? 'Published' : 'Draft'}
                      color={blog.isPublished ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{formatDate(blog.publishedAt)}</TableCell>
                  <TableCell>{blog.views ?? 0}</TableCell>
                  <TableCell align="right">
                    {canAccess('blogs', 'edit') && (
                      <IconButton color="primary" size="small" onClick={() => navigate(`/blogs/${blog._id}/edit`)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    )}
                    {canAccess('blogs', 'edit') && (
                      <IconButton
                        color={blog.isPublished ? 'warning' : 'success'}
                        size="small"
                        onClick={() => handleTogglePublish(blog)}
                      >
                        {blog.isPublished ? <UnpublishedIcon fontSize="small" /> : <PublishIcon fontSize="small" />}
                      </IconButton>
                    )}
                    {canAccess('blogs', 'delete') && (
                      <IconButton color="error" size="small" onClick={() => handleDelete(blog)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={total}
        page={page}
        onPageChange={(_event, nextPage) => setPage(nextPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(event) => {
          setRowsPerPage(Number(event.target.value));
          setPage(0);
        }}
        rowsPerPageOptions={[10, 20, 50]}
      />
    </Box>
  );
};

export default BlogList;
